import React from "react";
import AntdTable from "../../../components/common/AntdTable";
import { Card, Row, Col, Typography, Divider, Alert } from "antd";

const { Text, Title } = Typography;

const BalanceSheetTable = ({ balanceSheetData = {} }) => {
  const cashBankColumns = [
    {
      title: "ACCOUNT NAME",
      dataIndex: "accountName",
      width: 250,
    },
    {
      title: "ACCOUNT TYPE",
      dataIndex: "accountType",
      width: 150,
    },
    {
      title: "BALANCE",
      dataIndex: "balance",
      align: "right",
      width: 200,
      render: (balance) =>
        balance ? `UGX ${balance?.toLocaleString()}` : "UGX 0",
    },
  ];

  const fixedAssetsColumns = [
    {
      title: "ASSET NAME",
      dataIndex: "assetName",
      width: 300,
    },
    {
      title: "NET BOOK VALUE",
      dataIndex: "netBookValue",
      align: "right",
      width: 200,
      render: (value) =>
        value ? `UGX ${value?.toLocaleString()}` : "UGX 0",
    },
  ];

  const liabilitiesColumns = [
    {
      title: "ACCOUNT NAME",
      dataIndex: "accountName",
      width: 300,
    },
    {
      title: "BALANCE",
      dataIndex: "balance",
      align: "right",
      width: 200,
      render: (balance) =>
        balance ? `UGX ${balance?.toLocaleString()}` : "UGX 0",
    },
  ];

  // Extract data from response
  const assets = balanceSheetData?.assets || {};
  const cashBankItems = assets?.cashBank?.items || [];
  const totalCashBank = assets?.cashBank?.total || 0;
  const inventoryValue = assets?.inventory?.value || 0;
  const fixedAssetsItems = assets?.fixedAssets?.items || [];
  const totalFixedAssets = assets?.fixedAssets?.total || 0;
  const totalAssets = assets?.total || 0;

  const liabilities = balanceSheetData?.liabilities || {};
  const liabilityItems = liabilities?.items || [];
  const totalLiabilities = liabilities?.total || 0;

  const equity = balanceSheetData?.equity || {};
  const accumulatedFundOB = equity?.accumulatedFundOB || 0;
  const surplus = equity?.surplus || 0;
  const totalEquity = equity?.total || 0;

  const totals = balanceSheetData?.totals || {};
  const balanceCheck = totals?.balanceCheck || 0;
  const isBalanced = totals?.isBalanced || false;

  // Add totals row to cash bank
  const cashBankWithTotal = [
    ...cashBankItems,
    {
      accountName: "TOTAL CASH AT BANK/HAND",
      accountType: "",
      balance: totalCashBank,
      key: "cash-bank-total",
    },
  ];

  // Add totals row to fixed assets
  const fixedAssetsWithTotal = [
    ...fixedAssetsItems,
    {
      assetName: "TOTAL FIXED ASSETS",
      netBookValue: totalFixedAssets,
      key: "fixed-assets-total",
    },
  ];

  // Add totals row to liabilities
  const liabilitiesWithTotal = [
    ...liabilityItems,
    {
      accountName: "TOTAL LIABILITIES",
      balance: totalLiabilities,
      key: "liabilities-total",
    },
  ];

  return (
    <div>
      {/* Assets Section */}
      <Card
        size="small"
        title={<Title level={5} style={{ margin: 0 }}>ASSETS</Title>}
        style={{ marginBottom: 16 }}
      >
        {/* Cash at Bank/Hand */}
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: "14px" }}>
            Cash at Bank/Hand
          </Text>
          <AntdTable
            columns={cashBankColumns}
            pagination={false}
            data={cashBankWithTotal}
            rowClassName={(record) =>
              record.key === "cash-bank-total"
                ? "balance-sheet-total-row"
                : ""
            }
          />
        </div>

        {/* Inventory */}
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle" style={{ padding: "8px 0" }}>
            <Col>
              <Text strong>Inventory</Text>
              {assets?.inventory?.note && (
                <Text type="secondary" style={{ marginLeft: 8, fontSize: "12px" }}>
                  ({assets.inventory.note})
                </Text>
              )}
            </Col>
            <Col>
              <Text strong>UGX {inventoryValue.toLocaleString()}</Text>
            </Col>
          </Row>
        </div>

        {/* Fixed Assets */}
        <div>
          <Text strong style={{ fontSize: "14px" }}>
            Fixed Assets
          </Text>
          {assets?.fixedAssets?.note && (
            <Text type="secondary" style={{ marginLeft: 8, fontSize: "12px" }}>
              ({assets.fixedAssets.note})
            </Text>
          )}
          <AntdTable
            columns={fixedAssetsColumns}
            pagination={false}
            data={fixedAssetsWithTotal}
            rowClassName={(record) =>
              record.key === "fixed-assets-total"
                ? "balance-sheet-total-row"
                : ""
            }
          />
        </div>

        {/* Total Assets */}
        <Divider style={{ margin: "12px 0" }} />
        <Row justify="space-between" align="middle">
          <Col>
            <Text strong style={{ fontSize: "16px" }}>
              TOTAL ASSETS
            </Text>
          </Col>
          <Col>
            <Text strong style={{ fontSize: "16px" }}>
              UGX {totalAssets.toLocaleString()}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Liabilities Section */}
      <Card
        size="small"
        title={<Title level={5} style={{ margin: 0 }}>LIABILITIES</Title>}
        style={{ marginBottom: 16 }}
      >
        <AntdTable
          columns={liabilitiesColumns}
          pagination={false}
          data={liabilitiesWithTotal}
          rowClassName={(record) =>
            record.key === "liabilities-total"
              ? "balance-sheet-total-row"
              : ""
          }
        />
      </Card>

      {/* Equity Section */}
      <Card
        size="small"
        title={<Title level={5} style={{ margin: 0 }}>EQUITY</Title>}
        style={{ marginBottom: 16 }}
      >
        <Row justify="space-between" align="middle" style={{ padding: "8px 0" }}>
          <Col>
            <Text>Accumulated Fund (OB)</Text>
          </Col>
          <Col>
            <Text>UGX {accumulatedFundOB.toLocaleString()}</Text>
          </Col>
        </Row>
        <Row justify="space-between" align="middle" style={{ padding: "8px 0" }}>
          <Col>
            <Text>Surplus</Text>
          </Col>
          <Col>
            <Text
              style={{
                color: surplus >= 0 ? "#52c41a" : "#ff4d4f",
              }}
            >
              UGX {surplus.toLocaleString()}
            </Text>
          </Col>
        </Row>
        <Divider style={{ margin: "12px 0" }} />
        <Row justify="space-between" align="middle">
          <Col>
            <Text strong style={{ fontSize: "16px" }}>
              TOTAL EQUITY
            </Text>
          </Col>
          <Col>
            <Text strong style={{ fontSize: "16px" }}>
              UGX {totalEquity.toLocaleString()}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Balance Check Summary */}
      <Card
        size="small"
        style={{ marginTop: 16 }}
        bodyStyle={{ padding: "16px 24px" }}
      >
        <Row gutter={16} justify="space-between" align="middle">
          <Col>
            <Text strong style={{ fontSize: "16px" }}>
              Total Assets:{" "}
            </Text>
            <Text style={{ fontSize: "16px" }}>
              UGX {totalAssets.toLocaleString()}
            </Text>
          </Col>
          <Col>
            <Text strong style={{ fontSize: "16px" }}>
              Total Liabilities + Equity:{" "}
            </Text>
            <Text style={{ fontSize: "16px" }}>
              UGX {(totalLiabilities + totalEquity).toLocaleString()}
            </Text>
          </Col>
        </Row>
        <Divider style={{ margin: "12px 0" }} />
        {isBalanced ? (
          <Alert
            type="success"
            message="Balance Sheet is Balanced"
            description={`Assets (UGX ${totalAssets.toLocaleString()}) = Liabilities + Equity (UGX ${(
              totalLiabilities + totalEquity
            ).toLocaleString()})`}
            showIcon
          />
        ) : (
          <Alert
            type="warning"
            message="Balance Check"
            description={`Difference: UGX ${Math.abs(balanceCheck).toLocaleString()}`}
            showIcon
          />
        )}
      </Card>
    </div>
  );
};

export default BalanceSheetTable;



